import { memo } from 'react';
import { TrendingUp, Phone, UserCheck } from 'lucide-react';

export const MQLActivity = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading MQL activity data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { activities, summary } = data;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total MQLs</p>
              <p className="text-3xl font-bold text-blue-700 mt-2">
                {summary.totalMQLs}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Call Conversions</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                {summary.totalCallConversions}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {summary.avgCallConversionRate.toFixed(1)}% avg rate
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Phone className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Sales</p>
              <p className="text-3xl font-bold text-purple-700 mt-2">
                {summary.totalClientConversions}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {summary.avgClientConversionRate.toFixed(1)}% avg rate
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <UserCheck className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Activity Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  MQL Activity
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  MQLs
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Call Conversions
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Sales
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Call Rate
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Sales Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {activities.map((activity, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{activity.name}</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <p className="text-sm font-semibold text-gray-900">{activity.mqls}</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center">
                      <p className="text-sm font-semibold text-green-700">
                        {activity.callConversions}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center">
                      <p className="text-sm font-semibold text-purple-700">
                        {activity.clientConversions}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      activity.callConversionRate > 30 
                        ? 'bg-green-100 text-green-800'
                        : activity.callConversionRate > 20
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.callConversionRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      activity.clientConversionRate > 10 
                        ? 'bg-green-100 text-green-800'
                        : activity.clientConversionRate > 5
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.clientConversionRate.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-6 py-4 text-sm font-bold text-gray-900">
                  Total
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {summary.totalMQLs}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-green-700">
                  {summary.totalCallConversions}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-purple-700">
                  {summary.totalClientConversions}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {summary.avgCallConversionRate.toFixed(1)}%
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {summary.avgClientConversionRate.toFixed(1)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
});

MQLActivity.displayName = 'MQLActivity';